import type { MutationSource } from '../constants/mutation-constants';
import type { DataType, IData } from '../data';
import type { ITableChangeList } from '../model/table-data-model';
import type { DeltaOf } from '../utils/delta';

export enum ChangeType {
    // 执行监听时，会立即推送当前数据作为一次变更
    Init,
    // 来源于自身数据变化
    DataPatch,
    // 来源于继承链路的变化
    PropsInheritance,
    // 属性未发生变化，子列表节点发生变更
    ChildList,
    // 属性未发生变化，子映射表节点发生变更
    ChildMap,
}

export type DataModelInitChange<Data, Props> = {
    type: ChangeType.Init;
    diffs: {
        data: DeltaOf<Data>;
        props: DeltaOf<Props>;
    };
};

export type DataModelDataPatchChange<Data, Props> = {
    type: ChangeType.DataPatch;
    diffs: {
        data: DeltaOf<Data>;
        props: DeltaOf<Props>;
        tableChanges?: Readonly<ITableChangeList>;
    };
};

export type DataModelPropsInheritanceChange<Props> = {
    type: ChangeType.PropsInheritance;
    diffs: {
        props: DeltaOf<Props>;
        tableChanges?: Readonly<ITableChangeList>;
    };
};

export type DataModelChildListChange = {
    type: ChangeType.ChildList;
    diffs: {
        childList: {
            key: string;
        };
    };
};

export type DataModelChildMapChange = {
    type: ChangeType.ChildMap;
    diffs: {
        childMap: {
            key: string;
        };
    };
};

export type RawDataModelChange<Data = IData, Props = Data> =
    | DataModelInitChange<Data, Props>
    | DataModelDataPatchChange<Data, Props>
    | DataModelPropsInheritanceChange<Props>
    | DataModelChildListChange
    | DataModelChildMapChange;

export type DataModelChange<Data = IData, Props = Data> = RawDataModelChange<Data, Props> & { source: MutationSource };

export type DataModelChangeSubscriber<Data, Props> = (change: DataModelChange<Data, Props>) => void;

export type SubscribeOptions = {
    /*
     * 指定监听通知的优先级，值越大优先级越高，默认为 0
     */
    priority?: number;
    /*
     * 是否立即推送当前数据作为一次变更，默认为 false
     */
    initialChange?: boolean;
};

export type IObservableDataModel<Data = IData, Props = Data> = {
    id: string;
    type: DataType;
    props: Props;
    /**
     * 1. 新注册的监听器默认不会立即触发，除非显式设置 `options.initialChange` 为 `true`，此时将触发一次类型为 `init` 的初始化更新。
     * 2. 在同一事务中的多次数据修改，将合并为单次监听触发。
     *  - 创建或销毁的模型实例在事务内部不会触发监听。
     *  - 对于列表的多次变更，将不进行细粒度通知，而是合并为一次变更事件。
     *  - 如果事务中未涉及继承属性的修改，多次数据变动同样会被合并。
     *  - 当事务包含继承属性的变更时，仅会对该继承属性的变更发出一次通知。
     * 3. 对于表格数据，采用了特殊的差量变更通知机制，具体详情请参考 `DataModelDataPatchChange.diffs.tableChanges`。
     */
    subscribe(subscriber: DataModelChangeSubscriber<Data, Props>, options?: SubscribeOptions): () => void;
    unsubscribe(subscriber: DataModelChangeSubscriber<Data, Props>): void;
};

export type WithDataModelChangeNotifier<Data = IData, Props = Data> = {
    notify(...changes: DataModelChange<Data, Props>[]): void;
};
